import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import TutorialsCard, { TutorialsCardData } from './TutorialsCard';
import TutorialsViewModal from './TutorialsViewModal';

interface TutorialsCardFromDB {
  id: number;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

function TutorialsTab() {
  const { profile } = useAuth();
  const [cards, setCards] = useState<TutorialsCardFromDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<TutorialsCardData | null>(null);

  // Load published cards for partners
  const loadCards = async () => {
    if (!profile) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('tutorials-cards', {
        method: 'GET',
      });

      if (error) throw error;
      setCards(data?.cards || []);
    } catch (error) {
      console.error('Error loading tutorials:', error);
      toast.error('Failed to load tutorials');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCards();
  }, [profile]);

  // View card
  const handleViewCard = (card: TutorialsCardData) => {
    setSelectedCard(card);
    setViewModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading tutorials...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Tutorials</h2>
        <p className="text-sm text-muted-foreground">
          Learn how to effectively sell and promote our application to your clients.
        </p>
      </div>

      {cards.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No tutorials available</h3>
            <p className="text-muted-foreground">
              Tutorials will be available soon. Check back later.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <TutorialsCard
              key={card.id}
              id={card.id}
              title={card.title}
              description={card.description}
              content={card.content}
              display_order={card.display_order}
              is_published={card.is_published}
              created_at={card.created_at}
              updated_at={card.updated_at}
              mode="client"
              onView={handleViewCard}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <TutorialsViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        card={selectedCard}
        mode="client"
      />
    </div>
  );
}

export default TutorialsTab;